import { useId, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import { Avatar } from '@/components/Avatar/Avatar';
import { Button } from '@/components/Button/Button';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { TextField } from '@/components/TextField/TextField';
import { ApiError, NetworkError } from '@/lib/api';
import { t } from '@/lib/i18n';
import { formatPersonName } from '@/lib/format';
import {
  editFormSchema,
  editFormHumanSchema,
  type EditFormHumanValues,
  type EditFormValues,
  type EnhanceKind,
  type Member,
  type UpdateMemberRequest,
} from '../schemas';
import {
  useEnhance,
  useMember,
  usePositionsCatalog,
  useUnitsCatalog,
  useUpdateMember,
} from '../hooks';
import { EnhancePromptModal } from './EnhancePromptModal';
import { MultiItemPicker } from './MultiItemPicker';
import styles from './EditForm.module.css';

/**
 * Top-level edit route. Fetches the Member, handles loading/error states,
 * then dispatches to the right form variant based on kind. AI Members have
 * the full brief (instructions, personality, etc.); Human Members only
 * have editable assignments — their name/email come from the user profile.
 */
export function EditForm() {
  const { memberId } = useParams<{ memberId: string }>();
  const { data, isLoading, error } = useMember(memberId);

  if (isLoading) {
    return (
      <div className={styles.loading} role="status" aria-live="polite">
        {t('Loading…')}
      </div>
    );
  }

  if (error instanceof ApiError && error.status === 404) {
    return (
      <div className={styles.loading} role="alert">
        {t('Member not found.')}
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={styles.loading} role="alert">
        {t("Couldn't load this Member.")}
      </div>
    );
  }

  const { member } = data;
  return member.kind === 'human' ? (
    <EditFormHuman member={member} />
  ) : (
    <EditFormAi member={member} />
  );
}

// The form fields that have an Enhance with AI button. Mapping to the
// server-side EnhanceKind lives in ENHANCE_FIELD_TO_KIND below.
type EnhanceField = 'systemPrompt' | 'personality';

const ENHANCE_FIELD_TO_KIND: Record<EnhanceField, EnhanceKind> = {
  systemPrompt: 'member.instructions',
  personality: 'member.personality',
};

interface VariantProps {
  member: Member;
}

/* ─── AI Member edit form ─────────────────────────────────────────── */

function EditFormAi({ member }: VariantProps) {
  const navigate = useNavigate();
  const update = useUpdateMember();
  const enhance = useEnhance();
  const positionsCatalog = usePositionsCatalog();
  const unitsCatalog = useUnitsCatalog();
  const [formError, setFormError] = useState<string | null>(null);
  const [enhanceError, setEnhanceError] = useState<string | null>(null);
  // null when closed; otherwise the form field being enhanced. Drives which
  // EnhanceKind we send and which field gets the result written back.
  const [enhanceTarget, setEnhanceTarget] = useState<EnhanceField | null>(null);

  const descriptionId = useId();
  const descriptionErrorId = `${descriptionId}-err`;
  const personalityId = useId();
  const personalityErrorId = `${personalityId}-err`;
  const systemPromptId = useId();
  const systemPromptErrorId = `${systemPromptId}-err`;

  const form = useForm<EditFormValues>({
    resolver: zodResolver(editFormSchema),
    defaultValues: {
      first_name: member.first_name,
      last_name: member.last_name ?? '',
      description: member.description ?? '',
      personality: member.personality ?? '',
      systemPrompt: member.systemPrompt ?? '',
      positionIds: member.positions.map((p) => p.id),
      unitIds: member.units.map((u) => u.id),
    },
  });

  function onSubmit(values: EditFormValues) {
    setFormError(null);

    // Build a sparse PATCH body — only include keys the user actually
    // touched. RHF's dirtyFields is reliable for text inputs registered
    // with form.register(), but unreliable for Controller-managed arrays
    // (positionIds/unitIds) — so those get explicit value comparison.
    const dirty = form.formState.dirtyFields;
    const patch: UpdateMemberRequest = {};
    if (dirty.first_name) patch.first_name = values.first_name;
    if (dirty.last_name) {
      // Empty string in the form means "clear it" — send null.
      const trimmed = values.last_name?.trim() ?? '';
      patch.last_name = trimmed === '' ? null : trimmed;
    }
    if (dirty.description) {
      patch.description = values.description?.trim() ? values.description : null;
    }
    if (dirty.personality) {
      patch.personality = values.personality?.trim() ? values.personality : null;
    }
    if (dirty.systemPrompt) patch.systemPrompt = values.systemPrompt;
    if (
      !sameIdSet(
        values.positionIds,
        member.positions.map((p) => p.id),
      )
    ) {
      patch.positionIds = values.positionIds;
    }
    if (
      !sameIdSet(
        values.unitIds,
        member.units.map((u) => u.id),
      )
    ) {
      patch.unitIds = values.unitIds;
    }

    if (Object.keys(patch).length === 0) {
      void navigate(`/team/m/${encodeURIComponent(member.id)}`);
      return;
    }

    update.mutate(
      { memberId: member.id, patch },
      {
        onSuccess: () => {
          void navigate(`/team/m/${encodeURIComponent(member.id)}`);
        },
        onError: (err) => setFormError(formatPatchError(err)),
      },
    );
  }

  function openEnhanceFor(field: EnhanceField) {
    setEnhanceError(null);
    setEnhanceTarget(field);
  }

  function runEnhance(userRequest: string) {
    if (!enhanceTarget) return;
    setEnhanceError(null);
    const field = enhanceTarget;
    const current = form.getValues(field) ?? '';
    enhance.mutate(
      { kind: ENHANCE_FIELD_TO_KIND[field], content: current, userRequest },
      {
        onSuccess: (response) => {
          form.setValue(field, response.content, {
            shouldDirty: true,
            shouldValidate: true,
          });
          setEnhanceTarget(null);
        },
        onError: (err) => {
          if (err instanceof ApiError && err.status === 403) {
            setEnhanceError(t("You don't have permission to use Enhance with AI."));
          } else if (err instanceof NetworkError) {
            setEnhanceError(t("Couldn't reach the server. Check your connection and try again."));
          } else {
            setEnhanceError(t('Enhance failed. Try again.'));
          }
        },
      },
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('Edit {name}', { name: formatPersonName(member) })}
        subtitle={t('Tune the display details and assignments.')}
      />

      <form
        className={styles.form}
        onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}
        noValidate
      >
        <TextField
          label={t('First name')}
          autoComplete="off"
          error={form.formState.errors.first_name?.message}
          {...form.register('first_name')}
        />
        <TextField
          label={t('Last name')}
          autoComplete="off"
          hint={t('Optional.')}
          error={form.formState.errors.last_name?.message}
          {...form.register('last_name')}
        />

        <div className={styles.field}>
          <label htmlFor={descriptionId} className={styles.label}>
            {t('Description')}
          </label>
          <textarea
            id={descriptionId}
            rows={2}
            className={[styles.textarea, form.formState.errors.description ? styles.invalid : '']
              .filter(Boolean)
              .join(' ')}
            aria-invalid={form.formState.errors.description ? true : undefined}
            aria-describedby={form.formState.errors.description ? descriptionErrorId : undefined}
            {...form.register('description')}
          />
          {form.formState.errors.description ? (
            <div id={descriptionErrorId} className={styles.errorMessage} role="alert">
              {form.formState.errors.description.message}
            </div>
          ) : (
            <div className={styles.hint}>
              {t('A short, plain-English summary of what this Member does.')}
            </div>
          )}
        </div>

        <div className={styles.field}>
          <div className={styles.labelRow}>
            <label htmlFor={personalityId} className={styles.label}>
              {t('How they work')}
            </label>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => openEnhanceFor('personality')}
              disabled={enhance.isPending}
            >
              {t('Enhance with AI')}
            </Button>
          </div>
          <textarea
            id={personalityId}
            rows={4}
            className={[styles.textarea, form.formState.errors.personality ? styles.invalid : '']
              .filter(Boolean)
              .join(' ')}
            aria-invalid={form.formState.errors.personality ? true : undefined}
            aria-describedby={form.formState.errors.personality ? personalityErrorId : undefined}
            {...form.register('personality')}
          />
          {form.formState.errors.personality ? (
            <div id={personalityErrorId} className={styles.errorMessage} role="alert">
              {form.formState.errors.personality.message}
            </div>
          ) : (
            <div className={styles.hint}>
              {t('Personality and working style. Describe how you want them to behave.')}
            </div>
          )}
        </div>

        <div className={styles.field}>
          <div className={styles.labelRow}>
            <label htmlFor={systemPromptId} className={styles.label}>
              {t('Detailed instructions')}
            </label>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => openEnhanceFor('systemPrompt')}
              disabled={enhance.isPending}
            >
              {t('Enhance with AI')}
            </Button>
          </div>
          <textarea
            id={systemPromptId}
            rows={10}
            className={[styles.textarea, form.formState.errors.systemPrompt ? styles.invalid : '']
              .filter(Boolean)
              .join(' ')}
            aria-invalid={form.formState.errors.systemPrompt ? true : undefined}
            aria-describedby={form.formState.errors.systemPrompt ? systemPromptErrorId : undefined}
            {...form.register('systemPrompt')}
          />
          {form.formState.errors.systemPrompt ? (
            <div id={systemPromptErrorId} className={styles.errorMessage} role="alert">
              {form.formState.errors.systemPrompt.message}
            </div>
          ) : (
            <div className={styles.hint}>
              {t(
                'The full brief that guides how this Member responds. Enhance with AI rewrites your draft into a tighter, structured version.',
              )}
            </div>
          )}
        </div>

        <Controller
          control={form.control}
          name="positionIds"
          render={({ field }) => (
            <MultiItemPicker
              label={t('Positions')}
              hint={t('Positions this Member fills. Pick one or more.')}
              options={positionsCatalog.data?.items ?? []}
              value={field.value}
              onChange={(next) => field.onChange(next, { shouldDirty: true, shouldValidate: true })}
              isLoading={positionsCatalog.isLoading}
              emptyLabel={t('No Positions in your Library yet.')}
            />
          )}
        />

        <Controller
          control={form.control}
          name="unitIds"
          render={({ field }) => (
            <MultiItemPicker
              label={t('Departments')}
              hint={t('Departments this Member belongs to.')}
              options={unitsCatalog.data?.items ?? []}
              value={field.value}
              onChange={(next) => field.onChange(next, { shouldDirty: true, shouldValidate: true })}
              isLoading={unitsCatalog.isLoading}
              emptyLabel={t('No Departments in your Library yet.')}
            />
          )}
        />

        {formError ? (
          <div className={styles.formError} role="alert">
            {formError}
          </div>
        ) : null}

        <div className={styles.actions}>
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={() => void navigate(`/team/m/${encodeURIComponent(member.id)}`)}
          >
            {t('Cancel')}
          </Button>
          <Button type="submit" variant="primary" size="md" isLoading={update.isPending}>
            {update.isPending ? t('Saving…') : t('Save changes')}
          </Button>
        </div>
      </form>

      {enhanceTarget ? (
        <EnhancePromptModal
          fieldLabel={
            enhanceTarget === 'systemPrompt' ? t('Detailed instructions') : t('How they work')
          }
          isPending={enhance.isPending}
          error={enhanceError}
          onCancel={() => setEnhanceTarget(null)}
          onConfirm={runEnhance}
        />
      ) : null}
    </div>
  );
}

/* ─── Human Member edit form ──────────────────────────────────────── */

function EditFormHuman({ member }: VariantProps) {
  const navigate = useNavigate();
  const update = useUpdateMember();
  const positionsCatalog = usePositionsCatalog();
  const unitsCatalog = useUnitsCatalog();
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm<EditFormHumanValues>({
    resolver: zodResolver(editFormHumanSchema),
    defaultValues: {
      positionIds: member.positions.map((p) => p.id),
      unitIds: member.units.map((u) => u.id),
    },
  });

  function onSubmit(values: EditFormHumanValues) {
    setFormError(null);
    // Explicit value comparison — RHF's dirtyFields is unreliable for
    // Controller-managed array fields. See EditFormAi for context.
    const patch: UpdateMemberRequest = {};
    if (
      !sameIdSet(
        values.positionIds,
        member.positions.map((p) => p.id),
      )
    ) {
      patch.positionIds = values.positionIds;
    }
    if (
      !sameIdSet(
        values.unitIds,
        member.units.map((u) => u.id),
      )
    ) {
      patch.unitIds = values.unitIds;
    }

    if (Object.keys(patch).length === 0) {
      void navigate(`/team/m/${encodeURIComponent(member.id)}`);
      return;
    }

    update.mutate(
      { memberId: member.id, patch },
      {
        onSuccess: () => {
          void navigate(`/team/m/${encodeURIComponent(member.id)}`);
        },
        onError: (err) => setFormError(formatPatchError(err)),
      },
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('Edit {name}', { name: formatPersonName(member) })}
        subtitle={t('Update their team assignments.')}
      />

      <section className={styles.profileReadonly}>
        <Avatar name={formatPersonName(member)} size="lg" kind="human" />
        <div className={styles.profileReadonlyBody}>
          <span className={styles.profileReadonlyName}>{formatPersonName(member)}</span>
          {member.email ? (
            <span className={styles.profileReadonlyEmail}>{member.email}</span>
          ) : null}
          <span className={styles.hint}>
            {t("Name and email come from this person's account and aren't editable here.")}
          </span>
        </div>
      </section>

      <form
        className={styles.form}
        onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}
        noValidate
      >
        <Controller
          control={form.control}
          name="positionIds"
          render={({ field }) => (
            <MultiItemPicker
              label={t('Positions')}
              hint={t('Positions this Member fills. Pick one or more.')}
              options={positionsCatalog.data?.items ?? []}
              value={field.value}
              onChange={(next) => field.onChange(next, { shouldDirty: true, shouldValidate: true })}
              isLoading={positionsCatalog.isLoading}
              emptyLabel={t('No Positions in your Library yet.')}
            />
          )}
        />

        <Controller
          control={form.control}
          name="unitIds"
          render={({ field }) => (
            <MultiItemPicker
              label={t('Departments')}
              hint={t('Departments this Member belongs to.')}
              options={unitsCatalog.data?.items ?? []}
              value={field.value}
              onChange={(next) => field.onChange(next, { shouldDirty: true, shouldValidate: true })}
              isLoading={unitsCatalog.isLoading}
              emptyLabel={t('No Departments in your Library yet.')}
            />
          )}
        />

        {formError ? (
          <div className={styles.formError} role="alert">
            {formError}
          </div>
        ) : null}

        <div className={styles.actions}>
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={() => void navigate(`/team/m/${encodeURIComponent(member.id)}`)}
          >
            {t('Cancel')}
          </Button>
          <Button type="submit" variant="primary" size="md" isLoading={update.isPending}>
            {update.isPending ? t('Saving…') : t('Save changes')}
          </Button>
        </div>
      </form>
    </div>
  );
}

/* ─── Shared ──────────────────────────────────────────────────────── */

/** Set-equality on two id arrays (order-insensitive). */
function sameIdSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((s) => set.has(s));
}

function formatPatchError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 404) return t('This Member is no longer editable.');
    if (err.status === 403) return t("You don't have permission to edit Members.");
    return err.message;
  }
  if (err instanceof NetworkError) {
    return t("Couldn't reach the server. Check your connection and try again.");
  }
  return t('Something went wrong. Please try again.');
}
